import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AuthShell from '../components/auth/AuthShell';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore, useWorkspaceStore } from '../store';
import { Eye, EyeOff, ShieldCheck, Check } from 'lucide-react';
import { getApiErrorMessage } from '../lib/api';
import { authService, InvitationPreview } from '../services/authService';

const AcceptInvite: React.FC = () => {
  const { t } = useTranslation();
  const { acceptInvite, isAuthenticated, user } = useAuthStore();
  const { switchWorkspace } = useWorkspaceStore();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  // Alternative entry point: clicking a "pending workspace invitation"
  // notification (bell dropdown / toast) links here with an invitation id
  // instead of a token — notifications never carry the raw token (see
  // AuthService.notifyOfPendingInvitations). Only meaningful when
  // authenticated and there's no token; skips the by-token preview fetch
  // below in favor of a plainer "accept this invitation?" prompt, relying
  // on joinWorkspaceViaInvite's own error messages if anything's wrong.
  const directInvitationId = searchParams.get('invitationId') || '';

  // Preview — fetched regardless of auth state (public endpoint). Purely
  // informational for the logged-out name/password form below; it's the
  // whole basis of the logged-in flow, which skips that form entirely.
  const [preview, setPreview] = useState<InvitationPreview | null>(null);
  const [previewError, setPreviewError] = useState('');

  useEffect(() => {
    if (!token) return;
    authService.getInvitationPreview(token)
      .then(setPreview)
      .catch((error) => setPreviewError(getApiErrorMessage(error, t('auth.acceptInviteError'))));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Authenticated join flow state.
  const [isJoining, setIsJoining] = useState(false);
  const [joinError, setJoinError] = useState('');
  const [joinedWorkspace, setJoinedWorkspace] = useState<{ id: string; name: string } | null>(null);
  const [isSwitching, setIsSwitching] = useState(false);

  const emailMatches = !!(preview && user && preview.email.toLowerCase() === user.email.toLowerCase());

  const handleJoin = async () => {
    const invitationId = preview?.id || directInvitationId;
    if (!invitationId) return;
    setIsJoining(true);
    setJoinError('');
    try {
      const { workspace } = await authService.joinWorkspace(invitationId);
      setJoinedWorkspace(workspace);
    } catch (error) {
      setJoinError(getApiErrorMessage(error, t('auth.acceptInviteError')));
    } finally {
      setIsJoining(false);
    }
  };

  const handleSwitchNow = async () => {
    if (!joinedWorkspace) return;
    setIsSwitching(true);
    try {
      // Navigate to /dashboard FIRST (client-side, no reload yet) — the
      // store's switchWorkspace() reloads the page on success, and without
      // this we'd still be sitting on /accept-invite?token=... and just
      // reload right back into the same invite screen instead of landing
      // in the app.
      navigate('/dashboard');
      await switchWorkspace(joinedWorkspace.id);
    } catch {
      setIsSwitching(false);
    }
  };

  const [formData, setFormData] = useState({
    name: '',
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isLoading, setIsLoading] = useState(false);

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.name) {
      newErrors.name = t('errors.required');
    }

    if (!formData.password) {
      newErrors.password = t('auth.passwordRequired');
    } else if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9\s]).{8,}$/.test(formData.password)) {
      newErrors.password = t('auth.passwordComplexity');
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = t('errors.required');
    } else if (formData.confirmPassword !== formData.password) {
      newErrors.confirmPassword = t('auth.passwordMismatch');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      await acceptInvite(token, formData.name, formData.password);
      navigate('/dashboard');
    } catch (error) {
      console.error('Accept invite error:', error);
      setErrors({ general: getApiErrorMessage(error, t('auth.acceptInviteError')) });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  return (
    <AuthShell title={t('auth.acceptInviteTitle')} subtitle={t('auth.acceptInviteSubtitle')}>

        {!token && !directInvitationId ? (
          /* Missing token */
          <div className="space-y-6 bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-8">
            <div className="flex items-start gap-4 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-900/40 rounded-xl p-4">
              <ShieldCheck className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5 shrink-0" />
              <p className="text-sm text-red-600 dark:text-red-400">
                {t('auth.acceptInviteMissingToken')}
              </p>
            </div>
            <div className="text-center">
              <Link
                to="/login"
                className="font-medium text-brand-700 hover:text-brand-600"
              >
                {t('auth.backToLogin')}
              </Link>
            </div>
          </div>
        ) : isAuthenticated && !token ? (
          /* Reached via a "pending workspace invitation" notification —
             no token (notifications only ever carry the invitation id,
             never the raw token), so no preview to show. Straight to a
             generic confirmation; joinWorkspaceViaInvite's own errors
             (wrong account, expired, cap reached, ...) surface below. */
          <div className="space-y-6 bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-8">
            {joinedWorkspace ? (
              <div className="space-y-6">
                <div className="flex items-start gap-4 bg-green-50 dark:bg-green-900/20 border border-green-100 dark:border-green-900/40 rounded-xl p-4">
                  <Check className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5 shrink-0" />
                  <p className="text-sm text-green-700 dark:text-green-300">
                    {t('auth.acceptInviteJoined', { workspace: joinedWorkspace.name })}
                  </p>
                </div>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={handleSwitchNow}
                    disabled={isSwitching}
                    className="flex-1 py-2.5 px-4 text-sm font-medium rounded-xl text-brand-ink font-semibold bg-brand-400 hover:bg-brand-500 shadow-sm disabled:opacity-50"
                  >
                    {isSwitching ? t('auth.acceptInviteSwitching') : t('auth.acceptInviteSwitchNow')}
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate('/dashboard')}
                    className="flex-1 py-2.5 px-4 text-sm font-medium rounded-xl border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    {t('auth.acceptInviteStayHere')}
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  {t('auth.acceptInviteGenericPrompt')}
                </p>
                {joinError && (
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-900/40 rounded-xl p-4">
                    <p className="text-sm text-red-600 dark:text-red-400">{joinError}</p>
                  </div>
                )}
                <button
                  type="button"
                  onClick={handleJoin}
                  disabled={isJoining}
                  className="w-full flex justify-center py-2.5 px-4 text-sm font-medium rounded-xl text-brand-ink font-semibold bg-brand-400 hover:bg-brand-500 shadow-sm hover:shadow-md transition-all duration-200 disabled:opacity-50"
                >
                  {isJoining ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-brand-ink" />
                  ) : (
                    t('auth.acceptInviteJoinButton')
                  )}
                </button>
              </div>
            )}
          </div>
        ) : isAuthenticated ? (
          /* Already logged in — join with a single confirmation, no
             name/password needed (see AuthService.joinWorkspaceViaInvite). */
          <div className="space-y-6 bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-8">
            {joinedWorkspace ? (
              <div className="space-y-6">
                <div className="flex items-start gap-4 bg-green-50 dark:bg-green-900/20 border border-green-100 dark:border-green-900/40 rounded-xl p-4">
                  <Check className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5 shrink-0" />
                  <p className="text-sm text-green-700 dark:text-green-300">
                    {t('auth.acceptInviteJoined', { workspace: joinedWorkspace.name })}
                  </p>
                </div>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={handleSwitchNow}
                    disabled={isSwitching}
                    className="flex-1 py-2.5 px-4 text-sm font-medium rounded-xl text-brand-ink font-semibold bg-brand-400 hover:bg-brand-500 shadow-sm disabled:opacity-50"
                  >
                    {isSwitching ? t('auth.acceptInviteSwitching') : t('auth.acceptInviteSwitchNow')}
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate('/dashboard')}
                    className="flex-1 py-2.5 px-4 text-sm font-medium rounded-xl border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    {t('auth.acceptInviteStayHere')}
                  </button>
                </div>
              </div>
            ) : previewError ? (
              <div className="flex items-start gap-4 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-900/40 rounded-xl p-4">
                <ShieldCheck className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5 shrink-0" />
                <p className="text-sm text-red-600 dark:text-red-400">{previewError}</p>
              </div>
            ) : !preview ? (
              <div className="flex justify-center py-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
              </div>
            ) : !emailMatches ? (
              <div className="space-y-4">
                <div className="flex items-start gap-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-900/40 rounded-xl p-4">
                  <ShieldCheck className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
                  <p className="text-sm text-amber-700 dark:text-amber-300">
                    {t('auth.acceptInviteWrongAccount', { email: preview.email })}
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  {t('auth.acceptInvitePreview', { workspace: preview.workspace_name, role: t(`settings.profile.roles.${preview.role === 'client_user' ? 'clientUser' : preview.role}`) })}
                </p>
                {joinError && (
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-900/40 rounded-xl p-4">
                    <p className="text-sm text-red-600 dark:text-red-400">{joinError}</p>
                  </div>
                )}
                <button
                  type="button"
                  onClick={handleJoin}
                  disabled={isJoining}
                  className="w-full flex justify-center py-2.5 px-4 text-sm font-medium rounded-xl text-brand-ink font-semibold bg-brand-400 hover:bg-brand-500 shadow-sm hover:shadow-md transition-all duration-200 disabled:opacity-50"
                >
                  {isJoining ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-brand-ink" />
                  ) : (
                    t('auth.acceptInviteJoinButton')
                  )}
                </button>
              </div>
            )}
          </div>
        ) : (
          /* Form — logged out, creates a brand new account */
          <form className="space-y-6 bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-8" onSubmit={handleSubmit}>
            {preview && (
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t('auth.acceptInvitePreview', { workspace: preview.workspace_name, role: t(`settings.profile.roles.${preview.role === 'client_user' ? 'clientUser' : preview.role}`) })}
              </p>
            )}
            {errors.general && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-900/40 rounded-xl p-4">
                <p className="text-sm text-red-600 dark:text-red-400">{errors.general}</p>
              </div>
            )}

            <div className="space-y-4">
              {/* Full Name */}
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('auth.fullName')}
                </label>
                <div className="mt-1 relative">
                  <input
                    id="name"
                    name="name"
                    type="text"
                    autoComplete="name"
                    required
                    value={formData.name}
                    onChange={handleInputChange}
                    className={`input-field ${
                      errors.name ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''
                    }`}
                    placeholder="Enter your full name"
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                  )}
                </div>
              </div>

              {/* Password */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('auth.password')}
                </label>
                <div className="mt-1 relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    required
                    value={formData.password}
                    onChange={handleInputChange}
                    className={`input-field pr-10 ${
                      errors.password ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''
                    }`}
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-400" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                  {errors.password && (
                    <p className="mt-1 text-sm text-red-600">{errors.password}</p>
                  )}
                </div>
              </div>

              {/* Confirm Password */}
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('auth.confirmPassword')}
                </label>
                <div className="mt-1 relative">
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    required
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    className={`input-field pr-10 ${
                      errors.confirmPassword ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''
                    }`}
                    placeholder="Confirm your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-400" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                  {errors.confirmPassword && (
                    <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="btn-brand text-sm"
              >
                {isLoading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-brand-ink"></div>
                ) : (
                  <>
                    <ShieldCheck className="h-5 w-5 mr-2" />
                    {t('auth.acceptInviteTitle')}
                  </>
                )}
              </button>
            </div>
          </form>
        )}
    </AuthShell>
  );
};

export default AcceptInvite;
